<template>
  <div>
    <PageStyle>
      <template #pageContent>
        <div class="sm:flex justify-between items-center">
          <h2 class="mb-2 text-lg font-medium">
            Organization Branding
          </h2>
          <div class="sm:flex justify-between items-center">
            <button-with-spinner
              ref="saveButton"
              :disabled="!buttonEnabled || isSubmitting || isResetVisibilityRulesLoading || isPutEntitySupplementLoading"
              variant="secondary"
              :customClass="'inline-block text-center rounded font-semibold border-2 border-transparent px-8 min-h-10 text-white bg-teal-900 hover:bg-teal-100 hover:text-white focus:bg-teal-100 focus:text-white items-center justify-center w-full mt-4 xs:w-auto xs:mt-0'"
              @click="updateAndSaveWhiteLabelBranding"
            >
              {{ buttonText }}
            </button-with-spinner>
          </div>
        </div>

        <FormPanel
          :id="'urlPanel'"
          :title="'URL'"
          class="mt-6"
          panel-content-class="text-gray-600 border-gray-200"
        >
          <template #content>
            <div class="flex flex-col gap-4">
              <div>
                <label
                  data-testid="ownerLabel"
                  for="subdomain"
                  class="block text-sm font-medium text-gray-700"
                  ><span class="text-error-900 align-top">*</span> Owner</label
                >
                <SearchableSelect
                  :clear-search-on-blur="() => false"
                  :deselect-from-dropdown="true"
                  :loading="entitiesOptionsLoading"
                  :options="entitiesOptions.items"
                  placeholder="Select Owner"
                  label="name"
                  @search="fetchEntitiesOptions"
                  v-model="selectedOwnerEntity"
                  @option:selected="(val) => setSelectedEntityId(val)"
                >
                </SearchableSelect>
              </div>
              <BrandingURL
                :defaultUrl="brandingUrl"
                @branding-url="updateUrl"
              />
            </div>
          </template>
        </FormPanel>
        <FormPanel
          :id="'contentManagement'"
          :title="'Content Management'"
          class="mt-6"
          panel-content-class="text-gray-600 border-gray-200"
        >
          <template #content>
            <Spinner v-if="isResetVisibilityRulesLoading || isPutEntitySupplementLoading" outerClasses="flex justify-center items-center"/>
            <ContentManagement
              v-else
              v-model:excludePublicContent="excludePublicContent"
              v-model:aiAdvisingEnabled="aiAdvisingEnabled"
              :on-reset-decision="handleResetVisibilityRules"
            />
          </template>
        </FormPanel>
        <FormPanel
          :id="'logoPanel'"
          :title="'ORGANIZATION LOGO'"
          class="mt-6"
          panel-content-class="text-gray-600 border-gray-200"
        >
          <template #content>
            <ImageUpload
              ref="imageUploadRef"
              :name="'logo'"
              :file-types="['.jpeg', '.png', '.jpg']"
              :max-file-size="15"
              :custom-button-label="'Choose Logo'"
              :custom-file-box-label="'This logo will appear to learners throughout APIConnect. It will also show in emails and admin views. For best results, use a square logo version.'"
              :placeholder-img="existingUniversityLogo || undefined"
              @updateProgress="updateProgress"
              @updateFiles="updateFiles"
              @s3Location="updateLogoLocation"
              @imageRemoved="clearLogoLocation"
            />
          </template>
        </FormPanel>
        <FormPanel
          :id="'colorPanel'"
          :title="'ORGANIZATION COLORS'"
          class="mt-6"
          panel-content-class="text-gray-600 border-gray-200"
        >
          <template #content>
            <BrandingColour
              class="bg-white p-8 border-2 border-gray-200 rounded-lg"
              :defaultPrimary="primary"
              :defaultSecondary="secondary"
              :defaultAccent="accent"
              :value="organizationColorSelectionType"
              @branding-primary="updatePrimaryColor"
              @branding-secondary="updateSecondaryColor"
              @branding-accent="updateAccentColor"
              @invalid-primary="invalidPrimaryCheck"
              @invalid-secondary="invalidSecondaryCheck"
              @invalid-accent="invalidAccentCheck"
              @selected-option="checkIfColorIsDefault"
            />
          </template>
        </FormPanel>
        <FormPanel
          v-if="hasShowTenantPaymentProcessingEnabled"
          :id="'paymentProcessing'"
          :title="'PAYMENT PROCESSING'"
          class="mt-6"
          panel-content-class="text-gray-600 border-gray-200"
        >
          <template #titleText>
            <span class="inline-flex items-center gap-2">
              PAYMENT PROCESSING
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold tracking-wide"
                :class="paymentProcessingEnabled ? 'bg-teal-light text-teal-900' : 'bg-blue-400 text-blue-900'"
              >
                {{ paymentProcessingEnabled ? 'Configured' : 'Not Processed' }}
              </span>
            </span>
          </template>
          <template #content>
            <p>Choose how session payments are handled for this brand. This applies to every session under the brand unless overridden at the session level.</p>
            <div class="flex flex-col gap-6">
              <div class="flex flex-col gap-4 sm:flex-row mt-6 max-w-5xl">
                <label
                  class="flex-1 cursor-pointer rounded-lg border-2 p-5 transition-colors"
                  :class="paymentProcessingEnabled
                    ? 'border-teal-700 bg-teal-light'
                    : 'border-gray-200 bg-white hover:border-gray-300'"
                >
                  <div class="flex items-center gap-2">
                    <RadioButton
                      :model-value="paymentProcessingEnabled"
                      name="paymentProcessing"
                      :value="true"
                      :pt="radioButtonPt"
                      @click="turnOnPaymentProcessing"
                    />
                    <span class="text-lg font-bold text-blue-900">Process payments</span>
                  </div>
                  <p class="mt-2 text-sm">
                    Payments are collected through the platform and processed by the payment processor you selected below. Applies to every transaction under this brand.
                  </p>
                </label>

                <label
                  class="flex-1 cursor-pointer rounded-lg border-2 p-5 transition-colors"
                  :class="!paymentProcessingEnabled
                    ? 'border-teal-700 bg-teal-light'
                    : 'border-gray-200 bg-white hover:border-gray-300'"
                >
                  <div class="flex items-center gap-2">
                    <RadioButton
                      :model-value="paymentProcessingEnabled"
                      name="paymentProcessing"
                      :value="false"
                      :pt="radioButtonPt"
                      @click="promptTurnOffPaymentProcessing"
                    />
                    <span class="text-lg font-bold text-blue-900">Don't process payments</span>
                  </div>
                  <p class="mt-2 text-sm">
                    Payments aren't processed through any processor here. Orders and applications are still recorded, but transactions are handled elsewhere.
                  </p>
                </label>
              </div>
              <template v-if="paymentProcessingEnabled">
                <div class="max-w-lg">
                  <div class="flex items-center gap-1 mb-2">
                    <h2 class="text-lg font-bold text-blue-900">Payment Processor</h2>
                    <span
                      v-tooltip="'Stripe is currently the only supported payment processor for API Connect.'"
                      class="material-symbols-outlined text-gray-400 text-base cursor-help"
                    >help</span>
                  </div>
                  <div class="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <span class="font-bold text-blue-900">Stripe</span>
                    <span> — Default processor for API Connect</span>
                  </div>
                </div>
                <div class="max-w-lg">
                  <div class="flex items-center gap-2 mb-2">
                    <h2 class="text-lg font-bold text-blue-900">Processor Credentials</h2>
                    <span
                      class="inline-flex items-center gap-1 rounded-full bg-blue-400 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-900"
                    >
                      <span class="material-symbols-outlined text-sm leading-none">lock</span>
                      Coming soon
                    </span>
                  </div>
                  <input
                    type="text"
                    disabled
                    placeholder="Managed in a future release."
                    class="form-input block w-full min-h-10 cursor-not-allowed border-gray-200 bg-white text-gray-500 placeholder-gray-400"
                  />
                </div>
              </template>
          </div>
          <PaymentProcessingConfirmationModal
            :visible="showTurnOffPaymentProcessingModal"
            @update:visible="showTurnOffPaymentProcessingModal = $event"
            @confirm="confirmTurnOffPaymentProcessing"
            @cancel="cancelTurnOffPaymentProcessing"
          />
          </template>
        </FormPanel>
      </template>
    </PageStyle>
  </div>
</template>

<script setup>
import { datadogLogs } from "@datadog/browser-logs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import Dropdown from "primevue/dropdown";
import InputSwitch from "primevue/inputswitch";
import RadioButton from "primevue/radiobutton";
import { computed, onMounted, ref, toValue, unref, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import PageStyle from "@/components/forms/SharedComponents/Layout/SettingsPage.vue";
import FormPanel from "@/components/forms/SharedComponents/panel.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import { debounceSearchWrapper, useGetEntities } from "@/components/program-manager/sessions/composable";
import { entityTypes } from "@/components/program-manager/sessions/constants";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import {
  hasEnableContentMangementEnabled,
  hasShowTenantPaymentProcessingEnabled,
} from "@/composables/authorization.js";
import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "@/constants.js";
import forms from "@/mixins/forms";
import { useResetVisibilityRules } from "@/providers/entity_program_visibility/useResetVisibilityRules";
import { useGetSingleEntitySupplement } from "@/providers/entitySupplement/useGetSingleEntitySupplement";
import { usePutEntitySupplement } from "@/providers/entitySupplement/usePutEntitySupplement.ts";
import { getApiClient } from "@/services/API";
import entitiesService from "@/services/entities.js";
import whiteLabelBrandingService from "@/services/whiteLabelBrandingService";
import BrandingColour from "../../university/components/SiteComponents/branding/BrandingColour.vue";
import BrandingURL from "../../university/components/SiteComponents/branding/BrandingURL.vue";
import {
  API_DEFAULT_COLORS,
  ORGANIZATION_COLORS,
} from "../../university/components/SiteComponents/branding/constants.js";
import { getOrganizationColorSelectionType } from "../../university/components/SiteComponents/branding/utils.js";
import ButtonWithSpinner from "../forms/SharedComponents/ButtonWithSpinner.vue";
import ImageUpload from "../forms/SharedComponents/ImageUpload.vue";
import ContentManagement from "./ContentManagement.vue";
import { PAYMENT_PROCESSORS } from "./constants.ts";
import PaymentProcessingConfirmationModal from "./PaymentProcessingConfirmationModal/index.vue";
import { inputSwitchPt, radioButtonPt } from "./style.ts";
import { isValidSubdomain } from "./utils";

const {
  execute: executeGetEntities,
  state: entitiesOptions,
  isLoading: entitiesOptionsLoading,
} = useGetEntities({ immediate: true, throwError: false, resetOnExecute: true });

const fetchEntitiesOptions = debounceSearchWrapper(executeGetEntities, 250);

const store = useStore();
const toast = useToast();

const primary = ref("");
const secondary = ref("");
const accent = ref("");
const brandingUrl = ref("");
const logoPath = ref("");
const ipeds_id = ref("");
const savedBranding = ref("");
const selectedOption = ref("");
const buttonText = ref("Save");
const invalidPrimary = ref(false);
const invalidSecondary = ref(false);
const invalidAccent = ref(false);
const buttonEnabled = ref(false);
const isSubmitting = ref(false);
const selectedOwnerEntityId = ref(null);
const selectedOwnerEntity = ref(null);
const existingUniversityLogo = ref("");
const organizationColorSelectionType = ref("");
const imageUploadRef = ref(undefined);
const queryClient = useQueryClient();

const currentUser = computed(() => store.state.currentUser);

const excludePublicContent = ref(false);
const resetVisibilityRules = ref(false);

const aiAdvisingEnabled = ref(true);
const paymentProcessingEnabled = ref(true);
const showTurnOffPaymentProcessingModal = ref(false);

const { mutateAsync: executeResetVisibilityRules, isPending: isResetVisibilityRulesLoading } =
  useResetVisibilityRules();

const { mutateAsync: executePutEntitySupplement, isPending: isPutEntitySupplementLoading } = usePutEntitySupplement();

const { data: entitySupplementData } = useGetSingleEntitySupplement({
  id: selectedOwnerEntityId,
  useQueryOptions: {
    enabled: computed(() => !!selectedOwnerEntityId.value),
  },
});

watch(entitySupplementData, (newSupplement) => {
  aiAdvisingEnabled.value = newSupplement?.ai_advising_enabled ?? true;
  paymentProcessingEnabled.value = newSupplement?.payment_processing_enabled ?? true;
  if (newSupplement?.exclude_public_content) {
    excludePublicContent.value = newSupplement.exclude_public_content;
  } else {
    excludePublicContent.value = false;
  }
});

const whiteLabelBrandingQueryKey = computed(() => ["whiteLabelBranding", selectedOwnerEntityId.value]);
const { data: branding, isFetching } = useQuery({
  queryKey: whiteLabelBrandingQueryKey,
  queryFn: () => whiteLabelBrandingService.getByEntityId(selectedOwnerEntityId.value),
  refetchOnWindowFocus: false,
});

const { mutate: upsertBranding } = useMutation({
  mutationFn: (brandingData) => whiteLabelBrandingService.upsert(brandingData),
  onSuccess: (data) => {
    buttonBehavior("done");
    queryClient.invalidateQueries({ queryKey: whiteLabelBrandingQueryKey.value });
  },
  onError: (error) => {
    if (error.response.status === 409) {
      fireErrorToast("It looks like that subdomain is already in use, choose a different one");
    } else if (error.response.status === 400 && error.response.data?.error) {
      // The API rejects a subdomain the read path could never resolve; show its reason
      fireErrorToast(error.response.data.error);
    } else {
      fireErrorToast("An error occurred when trying to create/update white label data. Please try again later.");
    }
    buttonBehavior("failed");
  },
});

const handleResetVisibilityRules = (decision) => {
  resetVisibilityRules.value = decision;
};

const turnOnPaymentProcessing = () => {
  paymentProcessingEnabled.value = true;
};

const promptTurnOffPaymentProcessing = () => {
  showTurnOffPaymentProcessingModal.value = true;
};

const confirmTurnOffPaymentProcessing = () => {
  paymentProcessingEnabled.value = false;
  showTurnOffPaymentProcessingModal.value = false;
};

const cancelTurnOffPaymentProcessing = () => {
  showTurnOffPaymentProcessingModal.value = false;
};

const contentManagementPayload = computed(() => {
  return {
    exclude_public_content: excludePublicContent.value,
    reset_visibility_rules: resetVisibilityRules.value,
  };
});

const putEntitySupplementPayload = computed(() => ({
  ...(entitySupplementData?.value ?? {}),
  ai_advising_enabled: aiAdvisingEnabled.value ?? true,
  payment_processing_enabled: paymentProcessingEnabled.value ?? true,
  payment_processor: paymentProcessingEnabled.value ? PAYMENT_PROCESSORS.stripe : null,
}));

const setBranding = async () => {
  ipeds_id.value = selectedOwnerEntity?.value?.ipeds_id;
  selectedOwnerEntityId.value = branding?.value?.owner_entity_id;
  primary.value = branding?.value?.primary_color;
  secondary.value = branding?.value?.secondary_color;
  accent.value = branding?.value?.accent_color;
  brandingUrl.value = branding?.value?.subdomain;
  existingUniversityLogo.value = branding?.value?.logo;
  logoPath.value = branding?.value?.logo;
  organizationColorSelectionType.value = getOrganizationColorSelectionType(
    primary.value,
    secondary.value,
    accent.value,
  );
};

const resetBranding = () => {
  primary.value = API_DEFAULT_COLORS.PRIMARY;
  secondary.value = API_DEFAULT_COLORS.SECONDARY;
  accent.value = API_DEFAULT_COLORS.ACCENT;
  existingUniversityLogo.value = undefined;
  logoPath.value = undefined;
  brandingUrl.value = "";
  organizationColorSelectionType.value = ORGANIZATION_COLORS.API_CONNECT_DEFAULT;
  resetVisibilityRules.value = false;
  excludePublicContent.value = false;
  aiAdvisingEnabled.value = true;
  paymentProcessingEnabled.value = true;
};

watch(
  isFetching,
  (isCurrentlyFetching) => {
    if (!isCurrentlyFetching) {
      if (branding.value) {
        setBranding();
      } else {
        resetBranding();
      }
    }
  },
  { immediate: true },
);

const setSelectedEntityId = (entityVal) => {
  imageUploadRef.value?.removeAllFiles();
  selectedOwnerEntityId.value = entityVal.id;
};

const enableSave = () => {
  if (selectedOption.value === ORGANIZATION_COLORS.CUSTOM) {
    if (brandingUrl.value && (logoPath.value || existingUniversityLogo.value)) {
      if (
        invalidPrimary.value ||
        invalidSecondary.value ||
        invalidAccent.value ||
        !selectedOwnerEntityId.value ||
        !selectedOwnerEntity.value
      ) {
        buttonEnabled.value = false;
      } else buttonEnabled.value = true;
    } else {
      buttonEnabled.value = false;
    }
  } else if (
    brandingUrl.value &&
    (logoPath.value || existingUniversityLogo.value) &&
    selectedOwnerEntityId.value &&
    selectedOwnerEntity.value
  ) {
    buttonEnabled.value = true;
  } else {
    buttonEnabled.value = false;
  }
  if (buttonEnabled.value) buttonBehavior();
};

const buttonBehavior = (buttonState) => {
  switch (buttonState) {
    case "submitting":
      buttonText.value = "Submitting: Please Wait...";
      buttonEnabled.value = false;
      if (saveButton.value) saveButton.value.startLoading();
      break;
    case "done":
      buttonText.value = "Success!";
      buttonEnabled.value = true;
      if (saveButton.value) saveButton.value.stopLoading();
      break;
    case "failed":
      buttonText.value = "Failed: Please Retry";
      buttonEnabled.value = true;
      if (saveButton.value) saveButton.value.stopLoading();
      break;
    default:
      buttonText.value = "Save";
      buttonEnabled.value = true;
      if (saveButton.value) saveButton.value.stopLoading();
      break;
  }
};

const fireErrorToast = (errorMessage) => {
  toast.open({
    message: errorMessage,
    type: "error",
    position: "bottom",
    duration: ERROR_TIMEOUT,
  });
};

const fireSuccessfulToast = (successMessage) => {
  toast.open({
    message: successMessage,
    type: "success",
    position: "bottom",
    duration: SUCCESS_TIMEOUT,
  });
};

const updateAndSaveWhiteLabelBranding = async () => {
  const contentManagementPayloadSnapshot = { ...contentManagementPayload.value };
  const putEntitySupplementPayloadSnapshot = { ...putEntitySupplementPayload.value };
  if (!selectedOwnerEntityId.value) {
    toast.open({
      message: "You must choose an owner for this subdomain",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    return;
  }
  if (!brandingUrl.value) {
    toast.open({
      message: "You must put a valid subdomain in",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    return;
  }
  // A subdomain that is not a DNS label saves successfully but leaves the tenant
  // unreachable, so stop it here rather than reporting a save that cannot work
  if (!isValidSubdomain(brandingUrl.value)) {
    toast.open({
      message: "Subdomains can only contain letters, numbers, and hyphens, and cannot start or end with a hyphen",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    return;
  }
  buttonBehavior("submitting");
  let primaryColor = API_DEFAULT_COLORS.PRIMARY;
  let secondaryColor = API_DEFAULT_COLORS.SECONDARY;
  let accentColor = API_DEFAULT_COLORS.ACCENT;
  if (selectedOption.value === ORGANIZATION_COLORS.CUSTOM) {
    primaryColor = primary.value;
    secondaryColor = secondary.value;
    accentColor = accent.value;
  }
  const payload = {
    ipeds_id: ipeds_id?.value?.toString(),
    primary_color: primaryColor,
    secondary_color: secondaryColor,
    accent_color: accentColor,
    subdomain: brandingUrl.value,
    logo: logoPath.value || existingUniversityLogo.value,
    owner_entity_id: selectedOwnerEntityId.value,
  };
  if (hasEnableContentMangementEnabled.value) {
    await executeResetVisibilityRules(
      {
        entityId: selectedOwnerEntityId,
        payload: contentManagementPayloadSnapshot,
      },
      {
        onError: (error) => {
          fireErrorToast("Action failed. Contact your administrator for help or try again later.");
        },
        onSuccess: () => {
          fireSuccessfulToast("Success. Changes are now syncing to the student search.");
        },
      },
    );
  }
  await executePutEntitySupplement(
    {
      id: selectedOwnerEntityId,
      payload: putEntitySupplementPayloadSnapshot,
    },
    {
      onError: (error, variables) => {
        datadogLogs.logger.warn("Failed to update Entity Supplement.", {
          error: error.message,
          courseApprovalCount: unref(variables.id),
        });
        fireErrorToast(
          "Enable AI-supported Advising failed to sync, please try again later or contact an Administrator.",
        );
      },
    },
  );

  upsertBranding(payload);
};

const checkIfColorIsDefault = (option) => {
  selectedOption.value = option;
};

const updateUrl = (url) => {
  brandingUrl.value = url;
};

const invalidPrimaryCheck = (value) => {
  invalidPrimary.value = value;
};

const invalidSecondaryCheck = (value) => {
  invalidSecondary.value = value;
};

const invalidAccentCheck = (value) => {
  invalidAccent.value = value;
};

const updatePrimaryColor = (color) => {
  primary.value = color;
};

const updateSecondaryColor = (color) => {
  secondary.value = color;
};

const updateAccentColor = (color) => {
  accent.value = color;
};

const updateLogoLocation = (path) => {
  logoPath.value = path.url;
};

const clearLogoLocation = () => {
  logoPath.value = undefined;
};

const saveButton = ref(null);

watch(
  [
    primary,
    secondary,
    accent,
    invalidPrimary,
    invalidSecondary,
    invalidAccent,
    brandingUrl,
    logoPath,
    selectedOption,
    selectedOwnerEntityId,
    selectedOwnerEntity,
    paymentProcessingEnabled,
  ],
  enableSave,
);

onMounted(async () => {
  const { getSelectedEntityView: selectedEntity } = useTenantEntityManager();
  const initialEntityId = selectedEntity?.value?.id;
  selectedOwnerEntityId.value = initialEntityId;

  if (initialEntityId) {
    try {
      const initialEntityData = await entitiesService.getEntityById(initialEntityId);
      selectedOwnerEntity.value = initialEntityData?.data?.data?.items || {};
    } catch (error) {
      fireErrorToast("An Error occurred while fetching your University's data.");
    }
  }
});
</script>

<style scoped>
:deep(#vs1__combobox) {
  padding-right: 0px !important;
  padding-left: 0px !important;
}
</style>
